import { KeyValuePipe, TitleCasePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { environment } from '@environments/environment';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { ToastService } from '@static/services/toast.service';
import { saveAs } from 'file-saver-es';
import { FileSystemFileEntry, NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';

export interface ICompareResponse {
  file: string;
  summary: Record<string, ICompareResult>;
}

export interface ICompareResult {
  added: number;
  removed: number;
  changed: number;
}

@Component({
  selector: 'app-compare',
  imports: [
    MatButtonModule,
    MatCardModule,
    NgxFileDropModule,
    KeyValuePipe,
    TitleCasePipe,
  ],
  templateUrl: './compare.component.html',
  styleUrl: './compare.component.scss',
})
export class CompareComponent {
  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);
  private _toastService: ToastService = inject(ToastService);

  protected submitReady: boolean;
  protected results?: Record<string, ICompareResult>;

  protected baseName?: string;
  protected baseFile?: File;
  protected updatedName?: string;
  protected updatedFile?: File;

  constructor() {
    this.submitReady = false;
  }

  /**
   * Update the baseline file
   *
   * @param files drag drop file output
   */
  protected uploadBaseline(files: NgxFileDropEntry[]): void {
    if (!this._checkValidFile(files)) {
      this._snackService.openErrorSnack('Failed to process file');
      return;
    }
    this.baseName = undefined;
    this.baseFile = undefined;

    const fileEntry = files[0].fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      this.baseName = file.name;
      this.baseFile = file;
      this._checkValidFiles();
    });
  }

  /**
   * Update the updated file
   *
   * @param files drag drop file output
   */
  protected uploadLatest(files: NgxFileDropEntry[]): void {
    if (!this._checkValidFile(files)) {
      this._snackService.openErrorSnack('Failed to process file');
      return;
    }
    this.updatedName = undefined;
    this.updatedFile = undefined;

    const fileEntry = files[0].fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      this.updatedName = file.name;
      this.updatedFile = file;
      this._checkValidFiles();
    });
  }

  /**
   * Check if the upload file is valid and able to be used
   *
   * @param files file to check
   * @returns if the file is valid
   */
  private _checkValidFile(files: NgxFileDropEntry[]): boolean {
    return files.length === 1 ||
           files[0].fileEntry.isFile ||
           files[0].fileEntry.name.endsWith('.xer');
  }

  /**
   * Check if base and update XER files have been uploaded
   *
   * @returns if all files valid
   */
  private _checkValidFiles(): boolean {
    this.submitReady = false;
    if (this.baseFile && this.updatedFile) {
      this.submitReady = true;
      return true;
    }
    return false;
  }

  /**
   * Convert a base64 string into a valid file
   *
   * @param b64 base64 string
   * @returns promise of file blob
   */
  private async _b64ToBlob(b64: string): Promise<Blob> {
    const dataUrl = `data:text/plain;base64,${b64}`;
    const response = await fetch(dataUrl);
    return await response.blob();
  }

  /**
   * Submit the files to the webserver for processing
   */
  protected submitFiles(): void {
    // Check for files
    if (this.baseFile && this.updatedFile) {
      // Build form
      const formData = new FormData();
      formData.append('files', this.baseFile, this.baseFile.name);
      formData.append('files', this.updatedFile, this.updatedFile.name);

      // Send request and await resp
      const toastId = this._toastService.addToast('Comparing XER files...', undefined, true);
      this.results = undefined;
      this._httpService.postP<ICompareResponse>(
        `${environment.backendUri}/compare`,
        formData,
      )
        .then((res: ICompareResponse) => {
          this.results = res.summary;
          this._b64ToBlob(res.file)
            .then((file: Blob) => {
              saveAs(file, `Compare.xlsx`);
            });
        })
        .catch((err) => {
          if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
            this._snackService.openErrorSnack('Insufficient permissions for this project', err);
          }
          else this._snackService.openErrorSnack('Failed to compare', err);
        })
        .finally(() => {
          this._toastService.removeToast(toastId);
        });
    }
  }

  /**
   * Reset the display
   */
  protected reset(): void {
    this.results = undefined;
  }
}
